import sw from '@uswitch/ev-utils/switch'
import { noop, bind } from '../../../utils/utils'

/**
 * Enum of Table types
 * @enum {string}
 */
export const Types = { TH: 'th', TD: 'td' }

/**
 * Enum of List Classifications
 * @enum {string}
 */
export const ListC = { HEADERS: 'HEADERS', CELLS: 'CELLS', MIXED: 'MIXED' }

/**
 * Enum of Table direction classifications
 * @enum {string}
 */
export const TableC = { VERTICAL: 'VERTICAL', HORIZONTAL: 'HORIZONTAL', UNKNOWN: 'UNKNOWN' }

// ----------------------------------------
// ID for table headers & headers attribute
const heading = (r, c) => `h-${r}-${c}`
const cellIsTh = cell => cell.type === Types.TH
const cellIsTd = cell => cell.type === Types.TD

/**
 * rowsMap - Map a function over every row in a table
 * @param {[][]*} table - The 2D Table
 * @param {Function} f - Function to apply to each row
 * @returns {[]*} - Result of applying `f' to each row of `table'
 */
const rowsMap = (table, f = noop) => table.map(f)

/**
 * colsMap - Map a function over every column in a table
 * @param {[][]*} table - The 2D Table
 * @param {Function} f - Function to apply to each column
 * @returns {[]*} - Result of applying `f' to each column of `table'
 */
const colsMap = (table, f = noop) => table[0].map((_, col) => table.map(row => row[col])).map(f)

/**
 * tableMap - Map a function over every cell in a table
 * @param {[][]*} table - The 2D Table
 * @param {Function} f - Function to apply to each column
 * @returns {[][]*} - Result of applying `f' to each column of `table'
 */
const tableMap = (table, f = noop) =>
  table.map((row, idRow) => row.map((cell, idCol) => f(cell, idRow, idCol)))

/**
 * Classfies a list of cells by type of the cells as whether they are
 * entirely HEADERS, CELLS, or MIXED
 * @param {[]*} l - List of cells
 * @returns {[]ListC}
 */
const classifyList = l =>
  sw({
    [l.every(cellIsTh)]: ListC.HEADERS,
    [l.every(cellIsTd)]: ListC.CELLS,
    default: ListC.MIXED,
  })(true)

/**
 * Classifies a table based on cell type arrangement
 * Returns whether the table is
 *  - VERTICAL, headers go horizontally
 *  - HORIZONTAL, headers go vertically
 *  - UNKNOWN, no headers or odd headers
 * @param {[]ListC} rowsClass - The classification of the rows of the table
 * @param {[]ListC} colsClass - The classification of the columns of the table
 * @returns {TableC}
 */
const classifyTable = (rowsClass, colsClass) =>
  sw({
    [!rowsClass.some(t => t === ListC.MIXED)]: TableC.VERTICAL,
    [!colsClass.some(t => t === ListC.MIXED)]: TableC.HORIZONTAL,
    default: TableC.UNKNOWN,
  })(true)

/**
 * Find previous Table heading index
 * @param {[]ListC} list - List of classification values
 * @param {number} index - The index of the current item
 * @param {Function} [resF] - Callback function called with previousIndexn
 *
 * @example
 * prevThId(['CELL', 'HEADING', 'CELL'], 2) // Returns 1
 * prevThId(['HEADING', 'CELL', 'CELL'], 2) // Returns 0
 * prevThId(['CELL', 'CELL', 'CELL'], 2)    // Returns undefined
 *
 * @returns {(*|undefined)}
 */
const prevTHId = (list, index, resF = noop) => {
  const previousIndex = list
    .slice(0, index)
    .reduceRight((acc, it, i) => acc || (it === ListC.HEADERS ? i : null), null)

  return previousIndex !== null ? resF(previousIndex) : undefined
}

// ----------------------------------------

/**
 * Annotate table contents with inferred id & headers
 * attributes from the closest cell type.
 *
 * Classify rows & columns based on the contents of cells
 * [[<th />, <th />, <th />],
 *  [<td />, <td />, <td />],
 *  [<td />, <th />, <td />}]] -> [ 'HEADING', 'CELLS', 'MIXED' ]
 *
 * Use this to determine if table is VERTICAL or HORIZONTAL, i.e.
 * If classified rows have no MIXED, then it is VERTICAL
 * If classified cols have no MIXED, then it is HORIZONTAL
 *
 * Then iterate over each cell to and
 *  1. Add table position ids to <th /> cells, e.g. id="h-0-2"
 *  2. Use classification data to find nearest heading for <td /> cells
 *
 * @param {[][]*} table - 2D Table array
 * @returns {[][]*}
 */
export const annotateTableContents = table => {
  const rowsClassification = rowsMap(table, classifyList)
  const colsClassification = colsMap(table, classifyList)
  const isSimpleTable =
    rowsClassification[0] === ListC.HEADERS && // First row is all headers
    rowsClassification.slice(1).every(c => c === ListC.CELLS) // Other rows are all CELLS

  const tableClassification = classifyTable(rowsClassification, colsClassification)

  return tableMap(table, (cell, idRow, idCol) => {
    /* Search functions to find previous heading indexes, when
     * HORIZONTAL, search right-to-left when VERTICAL search
     * bottom-to-top */
    const prevHorizontalId = bind(prevTHId, colsClassification, idCol, i => heading(idRow, i))
    const prevVerticalId = bind(prevTHId, rowsClassification, idRow, i => heading(i, idCol))

    // `id` attribute is set on <th />, undefined removes attribute
    const id = cell.type === Types.TH ? heading(idRow, idCol) : undefined
    const headers = sw({
      [tableClassification === TableC.HORIZONTAL]: prevHorizontalId,
      [tableClassification === TableC.VERTICAL]: prevVerticalId,
      [isSimpleTable]: heading(0, idCol), // When simple single header table, we know the ID is 0th
      [cell.type === Types.TH]: undefined, // `headers` attribute only set on `<td />`
    })(true)

    return { ...cell, id, headers }
  })
}
